








































import Vue, { VueConstructor } from 'vue'
import Clickoutside from 'element-ui/src/utils/clickoutside'

export interface IDDTagPicker {
  $refs: {
    tagInputSelect: {
      query: string
      $refs: {
        input: HTMLElement
        tags: HTMLElement
      }
      currentPlaceholder: string
    }
  }
}

export default (Vue as VueConstructor<Vue & IDDTagPicker>).extend({
  name: 'TagsInput',

  directives: { Clickoutside },

  props: {
    selectedTags: {
      type: Array as () => Array<String>,
      default: (): Array<String> => [],
    },
    availableTags: {
      type: Array as () => Array<String>,
      default: (): Array<String> => [],
    },
    searchOnly: {
      type: Boolean,
      default: false,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    showInputBorder: {
      type: Boolean,
      default: false,
    },
    disableSelection: {
      type: Boolean,
      default: false,
    },
    enableDropdownAbsolutePositioning: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      filteredAvailableTags: [],
      curInputValue: '',
    }
  },

  watch: {
    availableTags: {
      immediate: true,
      handler(newValue) {
        this.filteredAvailableTags = newValue
      },
    },
  },

  computed: {
    internalSelectedTags: {
      get(): Array<String> {
        return this.selectedTags
      },
      set(newValue: Array<String>) {
        this.clearInputField()
        this.$emit('update:selectedTags', newValue)
      },
    },
    dropdownClass(): string {
      let result = 'tags-input__dropdown'
      if (this.enableDropdownAbsolutePositioning) {
        result += ' absolute'
      }
      return result
    },
  },

  mounted() {
    this.addInputPlaceholder()

    this.$refs.tagInputSelect.$refs.input.addEventListener(
      'keyup',
      (event: KeyboardEvent) => {
        this.adjustDropdownPosition()
      },
    )
    /**
     * Prevent dropdown when clicking on tags
     */
    this.$refs.tagInputSelect.$refs.tags.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
  },

  methods: {
    addInputPlaceholder() {
      const input: HTMLInputElement = this.$el.querySelector(
        '.el-select__input',
      )

      input.placeholder = this.isDisabled
        ? ''
        : this.searchOnly
          ? 'Select tags...'
          : 'Add tags...'
    },

    pruneAvailableTags(inputValue: string) {
      this.filteredAvailableTags = this.availableTags.filter(i =>
        i.toLowerCase().includes(inputValue.toLowerCase()),
      )
    },

    attemptTagSubmit(inputValue: string) {
      const inputValueCleansed = inputValue.toLowerCase().trim()

      if (
        !this.internalSelectedTags.includes(inputValueCleansed) &&
        inputValueCleansed !== ''
      ) {
        const updatedTags = this.internalSelectedTags
        updatedTags.push(inputValueCleansed)
        this.internalSelectedTags = updatedTags
      }
    },

    /**
     * Handle filter and comma to add new tag
     */
    handleCurInputValue(inputValue: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the 'this.$refs.tagInputSelect.query' is null whenever we
      lose focus*/
      this.curInputValue = inputValue

      this.adjustDropdownPosition()
      this.pruneAvailableTags(inputValue)
      if (this.searchOnly) {
        return
      }

      const inputNoComma = inputValue.slice(0, -1)
      // Treat comma as a delimiter
      if (inputValue.endsWith(',')) {
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptTagSubmit(inputNoComma)
        } else {
          this.clearInputField()
        }
      }
    },

    clearInputField() {
      this.curInputValue = ''
      this.$refs.tagInputSelect.query = ''
    },

    /**
     * Handle enter on a certain tag
     */
    handleDelimiter() {
      if (this.searchOnly) {
        this.clearInputField()
        return
      }

      const inputValue = this.$refs.tagInputSelect.query.trim()
      this.attemptTagSubmit(inputValue)
    },

    handleVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (!value && !this.searchOnly) {
        this.attemptTagSubmit(this.curInputValue)
      }
    },

    /**
     * Change position of dropdown to align with the beginning of the input
     */
    adjustDropdownPosition() {
      const input: HTMLInputElement = this.$el.querySelector(
        '.el-select__input',
      )
      const dropdown: HTMLElement = this.$el.querySelector(
        '.el-select-dropdown',
      )
      if (dropdown) {
        dropdown.style.marginLeft = input.offsetLeft + 'px'
      }
    },
  },
})
